import { Component, Input, inject } from '@angular/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { BalanceState, ContractBalance, ContractBalanceService } from './contract-balance.service';
import { AutoBuyDialog } from '../buy-contract/auto-buy-dialog/auto-buy-dialog';

interface StateView {
  label: string;
  icon: string;
  tone: 'success' | 'warning' | 'danger' | 'info' | 'neutral';
}

const STATE_VIEW: Record<BalanceState, StateView> = {
  add: { label: 'Add orders', icon: 'add_shopping_cart', tone: 'info' },
  'on-target': { label: 'On target', icon: 'balance', tone: 'success' },
  hold: { label: 'Hold new orders', icon: 'pan_tool', tone: 'warning' },
  'no-buyers': { label: 'No buy contracts', icon: 'block', tone: 'danger' },
  empty: { label: 'Nothing open', icon: 'hourglass_empty', tone: 'neutral' },
};

/**
 * Open orders (Pending + Transferring) vs. active supplier buy contracts,
 * against the admin-set ratio: at 2×, 10 buy contracts call for 20 orders.
 * Fewer says add orders, more says hold.
 * `full` is the dashboard panel (one summary row, details folded below),
 * `compact` is the header chip on contract pages.
 */
@Component({
  selector: 'app-contract-balance',
  imports: [AsyncPipe, DatePipe, MatIconModule, MatButtonModule, MatTooltipModule],
  templateUrl: './contract-balance.html',
  styleUrl: './contract-balance.scss',
})
export class ContractBalanceWidget {
  @Input() variant: 'full' | 'compact' = 'full';

  private service = inject(ContractBalanceService);
  private dialog = inject(MatDialog);
  readonly balance$ = this.service.balance$;
  detailsOpen = readDetailsOpen();

  view(b: ContractBalance): StateView {
    return STATE_VIEW[b.state];
  }

  advice(b: ContractBalance): string {
    const plan = `${plural(b.activeBuyContracts, 'buy contract')} × ${formatRatio(b.targetRatio)} = ${plural(b.targetOrders, 'order')}`;
    switch (b.state) {
      case 'empty':
        return 'No open orders and no active buy contracts.';
      case 'no-buyers':
        return `${b.activeOrders} open orders and no active buy contract to fill them. Hold new orders.`;
      case 'add':
        return `${plan}; ${b.activeOrders} open. Add ${plural(b.gapOrders, 'order')}.`;
      case 'on-target':
        return `${plan}; ${b.activeOrders} open. On target.`;
      case 'hold':
        return `${plan}; ${b.activeOrders} open, ${-b.gapOrders} over. Hold new orders.`;
    }
  }

  /**
   * Meter fill, 0–100. The target sits at the midpoint, so a fill reaching the
   * mark is on target and one past it is over.
   */
  fillShare(b: ContractBalance): number {
    if (!b.targetOrders) return b.activeOrders ? 100 : 0;
    return Math.min(100, Math.round((b.activeOrders / (2 * b.targetOrders)) * 100));
  }

  /** The pill's text: the verdict with its count where there is one. */
  pillLabel(b: ContractBalance): string {
    return b.state === 'add' ? `Add ${plural(b.gapOrders, 'order')}` : STATE_VIEW[b.state].label;
  }

  toggleDetails(): void {
    this.detailsOpen = !this.detailsOpen;
    try {
      localStorage.setItem(DETAILS_OPEN_KEY, String(this.detailsOpen));
    } catch {
      // Storage blocked: the panel still opens, it just won't be remembered.
    }
  }

  refresh(): void {
    this.service.refresh();
  }

  openAutoBuy(): void {
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(AutoBuyDialog, {
      width: isMobile ? '100vw' : '520px',
      maxWidth: isMobile ? '100vw' : '95vw',
      maxHeight: '90vh',
    }).afterClosed().subscribe(() => this.service.refresh());
  }

  formatRatio = formatRatio;
  formatRatioNumber = formatRatioNumber;
}

const DETAILS_OPEN_KEY = 'contractBalance.detailsOpen';

function readDetailsOpen(): boolean {
  try {
    return localStorage.getItem(DETAILS_OPEN_KEY) === 'true';
  } catch {
    return false;
  }
}

/** 2 → "2", 1.5 → "1.5". */
export function formatRatioNumber(r: number): string {
  return `${+r.toFixed(2)}`;
}

/** 2 → "2×", 1.5 → "1.5×". */
export function formatRatio(r: number): string {
  return `${+r.toFixed(2)}×`;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}
